import { useCallback, useState } from 'react'
import { supabase } from '../lib/supabaseClient'
import { useAuth } from '../contexts/AuthContext'
import { useAiThinkingEnabled } from './useAiThinkingEnabled'

// Thin wrapper around the ai-proxy edge function. Takes a `build`
// function that returns the request body the proxy expects — typically:
//   { prompt: '...', generationConfig: { ... } }
//
// Build runs at invocation time (not at hook construction) so it can
// pull fresh user data each call without us thrashing the network on
// every render.
//
// Returns the standard { run, reset, result, loading, error, debug }
// envelope plus the response shape `{ text, model, usage }` from the
// proxy.
//
// If `options.kind` is supplied, a successful response is INSERTed
// into ai_messages — the history panel reads from the same table.
// Insert failures are logged but don't surface as user-visible
// errors; the generation itself succeeded, the save is a nice-to-
// have. RLS scopes the row to the caller's user_id.
export function useAiCall(build, options = {}) {
  const { user } = useAuth()
  const [thinking] = useAiThinkingEnabled()
  const [result, setResult] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [debug, setDebug] = useState(null)
  const { kind, onPersisted } = options

  const run = useCallback(async () => {
    if (!user || typeof build !== 'function') return
    setLoading(true)
    setError(null)
    setResult(null)
    setDebug(null)
    const startedAt = Date.now()

    let invokeBody
    try {
      invokeBody = await build({ user, thinking })
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[ai] build() threw', e)
      setError(e?.message || 'failed to assemble prompt')
      setDebug({ elapsedMs: Date.now() - startedAt, threw: e?.message || String(e) })
      setLoading(false)
      return
    }
    if (!invokeBody || !invokeBody.prompt) {
      setError('build() returned no prompt')
      setLoading(false)
      return
    }

    // Apply the thinking toggle from settings unless the caller
    // overrode it explicitly in invokeBody.generationConfig.
    if (!invokeBody.generationConfig) invokeBody.generationConfig = {}
    if (invokeBody.generationConfig.thinkingBudget == null) {
      invokeBody.generationConfig.thinkingBudget = thinking ? -1 : 0
    }
    if (invokeBody.generationConfig.maxOutputTokens == null) {
      invokeBody.generationConfig.maxOutputTokens = thinking ? 4096 : 2048
    }

    try {
      const response = await supabase.functions.invoke('ai-proxy', { body: invokeBody })

      // eslint-disable-next-line no-console
      console.log('[ai] invoke response', {
        hasData: response.data != null,
        hasError: response.error != null,
        errorName: response.error?.name,
        errorMessage: response.error?.message,
      })

      const dbg = {
        elapsedMs: Date.now() - startedAt,
        hasError: !!response.error,
        errorName: response.error?.name || null,
        errorMessage: response.error?.message || null,
        dataKeys: response.data ? Object.keys(response.data) : null,
        contextStatus: null,
        contextStatusText: null,
        contextBody: null,
        thinking: invokeBody.generationConfig.thinkingBudget !== 0,
        model: invokeBody.model || 'gemini-flash-latest',
        promptChars: invokeBody.prompt.length,
      }

      if (response.error) {
        let detail = response.error.message || 'request failed'
        const inner = response.error.context
        if (inner && typeof inner === 'object') {
          dbg.contextStatus = inner.status ?? null
          dbg.contextStatusText = inner.statusText ?? null
          if (typeof inner.text === 'function') {
            try {
              const raw = await inner.text()
              dbg.contextBody = raw || ''
              try {
                const parsed = JSON.parse(raw)
                if (parsed?.error) detail = parsed.error
                if (parsed?.detail) detail += ` — ${parsed.detail}`
              } catch {
                if (raw) detail = raw.slice(0, 300)
              }
            } catch (bodyErr) {
              dbg.contextBody = `<text() threw: ${(bodyErr || {}).message || bodyErr}>`
            }
          }
        }
        setError(detail)
      } else if (response.data?.error) {
        setError(response.data.error)
      } else {
        setResult(response.data)

        // Auto-persist successful generations when a kind was
        // declared. Done after setResult so the UI updates first;
        // a save failure shouldn't block rendering the answer.
        if (kind && response.data?.text) {
          try {
            const { error: insErr } = await supabase.from('ai_messages').insert({
              user_id: user.id,
              kind,
              text: response.data.text,
              model: response.data.model || null,
              usage: response.data.usage || null,
              prompt_chars: invokeBody.prompt.length,
            })
            if (insErr) {
              // eslint-disable-next-line no-console
              console.error('[ai] ai_messages insert failed:', insErr)
            } else if (typeof onPersisted === 'function') {
              onPersisted()
            }
          } catch (persistErr) {
            // eslint-disable-next-line no-console
            console.error('[ai] ai_messages insert threw:', persistErr)
          }
        }
      }

      setDebug(dbg)
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[ai] threw outside invoke envelope', e)
      setError(e?.message || 'request failed')
      setDebug({
        elapsedMs: Date.now() - startedAt,
        threw: e?.message || String(e),
        stack: e?.stack ? String(e.stack).split('\n').slice(0, 8).join('\n') : null,
      })
    } finally {
      setLoading(false)
    }
  }, [user, build, thinking, kind, onPersisted])

  const reset = useCallback(() => {
    setResult(null)
    setError(null)
    setDebug(null)
  }, [])

  return { run, reset, result, loading, error, debug }
}
